import { Link } from "react-router-dom";
import { ArrowRight, Shield, Clock, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FranceCoverage from "@/components/FranceCoverage";

const HERO_BG =
  "https://images.pexels.com/photos/261985/pexels-photo-261985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONVOYAGE_IMG =
  "https://images.pexels.com/photos/34539243/pexels-photo-34539243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const VTC_IMG =
  "https://images.unsplash.com/photo-1616805111699-0e52fa62f779?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwY2hhdWZmZXVyJTIwZHJpdmluZ3xlbnwwfHx8fDE3ODQ4MzQ5MDZ8MA&ixlib=rb-4.1.0&q=85";
const NETTOYAGE_IMG =
  "https://images.pexels.com/photos/6873011/pexels-photo-6873011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEPANNAGE_IMG =
  "https://images.pexels.com/photos/6870319/pexels-photo-6870319.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#050505]">
      <Header />
      {/* HERO */}
      <section
        className="relative pt-32 pb-40 overflow-hidden grain"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,5,5,.35) 0%, rgba(5,5,5,.85) 55%, #050505 100%), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <div className="text-xs tracking-[0.55em] gold uppercase mb-6">
              — Depuis {new Date().getFullYear() - 1} • Premium
            </div>
            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Le transport de <span className="gold">l'excellence</span>.<br />
              Quatre services, une exigence.
            </h1>
            <p className="mt-8 text-lg text-gray-300 max-w-2xl leading-relaxed">
              DAS SIMAO AUTO convoie vos véhicules et transporte vos passagers
              partout en France, avec la même exigence de discrétion, de
              ponctualité et de soin. Tarifs transparents, réservation en
              quelques clics, validation manuelle par nos soins.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/services/convoyage"
                data-testid="hero-cta-convoyage"
                className="btn-gold inline-flex items-center gap-2"
              >
                Convoyer un véhicule <ArrowRight size={18} />
              </Link>
              <Link
                to="/services/vtc"
                data-testid="hero-cta-vtc"
                className="btn-outline-gold inline-flex items-center gap-2"
              >
                Réserver un VTC <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Validation manuelle", text: "Chaque réservation confirmée personnellement." },
              { icon: Clock, title: "Disponible 7j/7", text: "Y compris nuits, dimanches et jours fériés." },
              { icon: Sparkles, title: "Prix transparents", text: "Tarif au kilomètre affiché en temps réel." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-6 border border-white/10 bg-black/40 backdrop-blur-sm"
              >
                <Icon size={22} className="gold mb-3" />
                <div className="text-white font-medium">{title}</div>
                <div className="text-sm text-gray-400 mt-1">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three service cards */}
      <section className="relative -mt-24 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="text-xs tracking-[0.5em] gold uppercase">Nos services</div>
            <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl">
              Quatre services disponibles.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ChoiceCard
              testId="choice-convoyage"
              to="/services/convoyage"
              image={CONVOYAGE_IMG}
              eyebrow="Service #01"
              title="Convoyage de véhicule"
              description="Livraison de votre auto de A à B. Roulant ou non, boîte manuelle ou auto, partout en France."
              price="dès 1,00€ / km"
              cta="Découvrir"
            />
            <ChoiceCard
              testId="choice-vtc"
              to="/services/vtc"
              image={VTC_IMG}
              eyebrow="Service #02"
              title="Trajet VTC"
              description="Chauffeur privé pour vos trajets ponctuels ou vos mises à disposition. Discrétion et ponctualité."
              price="dès 20€ prise en charge"
              cta="Découvrir"
            />
            <ChoiceCard
              testId="choice-nettoyage"
              to="/services/nettoyage"
              image={NETTOYAGE_IMG}
              eyebrow="Service #03"
              title="Nettoyage automobile"
              description="Nettoyage intérieur et extérieur soigné. Formules à la carte, pour donner à votre voiture tout son éclat."
              price="dès 25 €"
              cta="Découvrir"
            />
            <ChoiceCard
              testId="choice-depannage"
              to="/services/depannage"
              image={DEPANNAGE_IMG}
              eyebrow="Service #04"
              title="Dépannage"
              description="Dépannage et remorquage 24h/24, 7j/7. Panne, crevaison, batterie — nous venons à votre secours."
              price="bientôt disponible"
              cta="En savoir plus"
              comingSoon
            />
          </div>
        </div>
      </section>

      {/* Pricing rules */}
      <section className="py-24 border-t border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Tarifs Convoyage</div>
            <h3 className="mt-3 font-serif text-white text-3xl mb-6">
              Un prix juste, dégressif par palier.
            </h3>
            <PricingRow label="0 – 100 km" price="1,30 €" per="/ km" />
            <PricingRow label="101 – 300 km" price="1,10 €" per="/ km" />
            <PricingRow label="+ 300 km" price="1,00 €" per="/ km" />
            <p className="mt-6 text-sm text-gray-400">
              Acompte de 30% à la réservation, solde avant / à la livraison du véhicule.
            </p>
          </div>
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Tarifs VTC</div>
            <h3 className="mt-3 font-serif text-white text-3xl mb-6">
              Trajet ou mise à disposition.
            </h3>
            <PricingRow label="Trajet — Prise en charge" price="20 €" per="fixe" />
            <PricingRow label="Trajet — Distance" price="1,30 → 1,00 €" per="/ km" />
            <PricingRow label="Mise à disposition" price="45 €" per="/ h (min. 2h)" />
            <PricingRow label="Nuit 22h – 06h" price="55 €" per="/ h" />
            <p className="mt-6 text-sm text-gray-400">
              Majoration +20% dimanche & jours fériés. Trajet payé à 100% à la
              réservation, mise à disposition en acompte 30%.
            </p>
          </div>
        </div>
      </section>

      <FranceCoverage />

      <Footer />
    </div>
  );
}

function ChoiceCard({ testId, to, image, eyebrow, title, description, price, cta, comingSoon }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="relative overflow-hidden border border-white/10 hover:border-[color:var(--gold)]/60 transition-all duration-500 group block h-[520px]"
    >
      <div
        className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-110"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,5,5,.15) 0%, rgba(5,5,5,.9) 75%), url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {comingSoon && (
        <div className="absolute top-5 right-5 z-20 inline-flex items-center gap-2 border border-[color:var(--gold)]/60 bg-black/70 backdrop-blur-sm gold px-3 py-1.5 text-[10px] tracking-[0.35em] uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] animate-pulse" />
          Bientôt
        </div>
      )}
      <div className="relative z-10 h-full flex flex-col justify-end p-8">
        <div className="text-xs tracking-[0.4em] gold uppercase mb-2">{eyebrow}</div>
        <h3 className="font-serif text-3xl text-white mb-3 group-hover:translate-x-1 transition-transform duration-500">
          {title}
        </h3>
        <p className="text-sm text-gray-300 max-w-md mb-5 leading-relaxed">
          {description}
        </p>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">
              {comingSoon ? "Disponibilité" : "à partir de"}
            </div>
            <div className="text-white text-lg font-serif gold whitespace-nowrap">{price}</div>
          </div>
          <div className="btn-gold inline-flex items-center gap-1.5 text-sm px-4 py-2.5 shrink-0">
            {cta} <ArrowRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function PricingRow({ label, price, per }) {
  return (
    <div className="flex items-baseline justify-between py-4 border-b border-white/5">
      <span className="text-gray-300">{label}</span>
      <span>
        <span className="font-serif text-2xl text-white">{price}</span>
        <span className="text-gray-500 text-sm ml-2">{per}</span>
      </span>
    </div>
  );
}
